import { baseClient } from "./baseRequest";
import { Klydo } from "./klydo";

export type GiftCategory = {
  _id: string;
  name: string;
  index: number;
  categoryImageUrl: string;
  pendulumLogoUrl?: string;
  klydoIds: string[];
  klydos?: Klydo[];
};

export type PopulatedGift = {
  orderName: string;
  clocksIds?: string[];
  senderName: string;
  greetingContent: string;
  pendulumLogoUrl?: string;
  klydo: Klydo;
  date?: number;
  senderImageUrl?: string;
  timeZone?: string;
};

export type OrderInfo = {
  email: string;
  first_name: string;
  last_name: string;
  info: {
    serial_number: string[];
  }[];
  daysToConversion: number;
  firstVisit: {
    landingPage: string;
    occurredAt: string;
    referrerUrl: string;
  };
};

export type Gift = {
  orderName: string;
  createdAt: Date;
  clocksIds?: string[];
  senderName: string;
  giftId?: string;
  greetingContent: string;
  klydoId: string;
  manualOverride?: boolean;
  date?: number | null;
  senderImageUrl?: string;
};

export type PendulumLogo = {
  id: string;
  url: string;
  filename: string;
  createdAt: Date;
  name?: string;
  description?: string;
};

export type ListGiftOrdersInput = {
  skip: number;
  limit: number;
  query?: string;
};

export type EditGiftInput = { updatedOrderName: string; gift: Partial<Gift> };

export type GiftCategoriesClient = {
  getAllCategories: () => Promise<GiftCategory[]>;
  createCategory: (category: Partial<GiftCategory>) => Promise<string>;
  getOrderInfo(orderName: string): Promise<OrderInfo>;
  getOrderGift: (orderName: string) => Promise<Gift>;
  deleteOrderGift: (orderName: string) => Promise<void>;

  updateCategory: (
    categoryId: string,
    category: Partial<GiftCategory>,
  ) => Promise<void>;
  removeCategory: (categoryId: string) => Promise<void>;
  deleteClockGift: (clockId: string) => Promise<void>;
  getClockGift: (clockId: string) => Promise<PopulatedGift | null>;
  listGiftOrders: (input: ListGiftOrdersInput) => Promise<Gift[]>;
  editOrderGift: (input: EditGiftInput) => Promise<Gift>;
  getLogos: () => Promise<PendulumLogo[]>;
  createLogo: (logo: Partial<PendulumLogo>) => Promise<PendulumLogo>;
  updateLogo: (id: string, logo: Partial<PendulumLogo>) => Promise<void>;
  deleteLogo: (id: string) => Promise<void>;
};

const giftCategoriesClient: GiftCategoriesClient = {
  editOrderGift: async (input) => {
    return baseClient.post({
      url: "/admin/gifts/edit-order-gift",
      body: input,
    });
  },
  listGiftOrders: async (input) => {
    return baseClient.post<Gift[]>({
      url: "/admin/gifts/list-gift-orders",
      body: input,
    });
  },
  getOrderInfo: async (orderName) => {
    return baseClient.get({
      url: `/orders/${orderName}`,
    });
  },
  getOrderGift: async (orderName: string) => {
    return baseClient.get<Gift>({
      url: "/admin/gifts/get-order-gift",
      params: {
        orderName,
      },
    });
  },
  deleteOrderGift: async (orderName: string) => {
    return baseClient.delete({
      url: `/admin/gifts/${orderName}`,
    });
  },
  deleteClockGift: async (clockId: string) => {
    return baseClient.delete({
      url: `/admin/gifts?clockId=${clockId}`,
    });
  },
  getClockGift: async (clockId: string) => {
    return baseClient.get<PopulatedGift | null>({
      url: "/admin/gifts/get-clock-gift",
      params: {
        ["clock-id"]: clockId,
      },
    });
  },
  getAllCategories: async () => {
    return baseClient.get<GiftCategory[]>({
      url: "/gift-categories",
    });
  },
  createCategory: async (category) => {
    return baseClient.post<string>({
      url: "/gift-categories",
      body: category,
    });
  },
  updateCategory: async (categoryId, category) => {
    return baseClient.put({
      url: `/gift-categories/${categoryId}`,
      body: category,
    });
  },
  removeCategory: async (categoryId) => {
    return baseClient.delete({
      url: `/gift-categories/${categoryId}`,
    });
  },
  getLogos: async () => {
    return baseClient.get<PendulumLogo[]>({
      url: "/gift-categories/logos",
    });
  },
  createLogo: async (logo) => {
    return baseClient.post<PendulumLogo>({
      url: "/gift-categories/logos",
      body: logo,
    });
  },
  updateLogo: async (id, logo) => {
    return baseClient.put({
      url: `/gift-categories/logos/${id}`,
      body: logo,
    });
  },
  deleteLogo: async (id) => {
    return baseClient.delete({
      url: `/gift-categories/logos/${id}`,
    });
  },
};

export default giftCategoriesClient;
